import { Box, Button, Divider, Group, Input, Skeleton, Stack, Text, TextInput, Title } from "@mantine/core";
import { modals } from "@mantine/modals";
import { Suspense } from "react";
import { Section, SectionProvider } from "shared/components/global/Section";
import { AcknowledgementsSwitch } from "shared/components/message/compose/AcknowledgementsSwitch";
import { AttachmentEditor } from "shared/components/message/compose/AttachmentEditor";
import { AudienceGeoEditor } from "shared/components/message/compose/AudienceGeoEditor";
import { AudienceGroupEditor } from "shared/components/message/compose/AudienceGroupsEditor";
import { AudienceTopicEditor } from "shared/components/message/compose/AudienceTopicEditor";
import { AudienceUserEditor } from "shared/components/message/compose/AudienceUserEditor";
import { BodyPreview } from "shared/components/message/compose/BodyPreview";
import { ComposeFormProvider, useComposeFormContext } from "shared/components/message/compose/context";
import { CustomizationEditor } from "shared/components/message/compose/CustomizationEditor";
import { DurationEditor } from "shared/components/message/compose/DurationEditor";
import { MessageBodyEditor } from "shared/components/message/compose/MessageBodyEditor";
import { PollEditor } from "shared/components/message/compose/PollEditor";
import { SenderSelect } from "shared/components/message/compose/SenderSelect";
import { SmsPreview } from "shared/components/message/compose/SmsPreview";
import { UrgentSwitch } from "shared/components/message/compose/UrgentSwitch";
import { useComposeFormCmSelection } from "shared/components/message/compose/useComposeFormCmSelection";
import { CmOverrides } from "shared/components/message/MessageOptionsEditor";
import { features } from "shared/graphql/features";
import { useFormState } from "shared/hooks/useFormState";
import { useIsSupport } from "shared/hooks/useIsSupport";
import { Cm } from "src/gql/graphql";

export const ComposeForm = () => {
    const { isSupport } = useIsSupport();
    const form = useComposeFormContext();

    const hasTopics = features.enabled("HAS_TOPICS");
    const hasOverrides = features.enabled("HAS_CM_DELIVERY_OVERRIDES");

    const [duration, setDuration] = useFormState(form, "duration");
    const { hasAny, isUrgent } = useComposeFormCmSelection();

    const hasSmsPreview = hasAny(Cm.Sms) || !hasOverrides;

    return (
        <SectionProvider>
            <Stack mb="xs">
                <SenderSelect />

                <div>
                    <Text fw="bold">Message Duration</Text>
                    <Text size="xs" c="dimmed">
                        Message status will be marked as past...
                    </Text>
                </div>
                <DurationEditor
                    value={duration}
                    minDate={new Date()}
                    onChange={(value) => {
                        setDuration(value);
                        form.clearFieldError("duration.absolute");
                    }}
                    error={form.getInputProps("duration.absolute").error}
                />
            </Stack>

            <Section label="Audience">
                <AudienceUserEditor />
                <Stack>
                    <AudienceGroupEditor />
                    {hasTopics && <AudienceTopicEditor />}
                    <AudienceGeoEditor />
                </Stack>
            </Section>
            <Section label="Message">
                <UrgentSwitch />
                {isUrgent && <AcknowledgementsSwitch />}
                {hasOverrides && <CmOverrides mode="form" />}
                <TextInput required label="Subject" {...form.getInputProps("subject")} />
                <Input.Wrapper required label="Body">
                    <Box
                        style={{
                            border: "solid 1px var(--mantine-color-default-border)",
                            borderRadius: "6px",
                        }}
                    >
                        <MessageBodyEditor />
                    </Box>
                </Input.Wrapper>

                <CustomizationEditor />

                {hasSmsPreview && (
                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            onClick={() => {
                                if (form.validate().hasErrors) return;

                                modals.open({
                                    title: <Title order={3}>SMS Preview</Title>,
                                    children: (
                                        <ComposeFormProvider form={form}>
                                            <SmsPreview />
                                        </ComposeFormProvider>
                                    ),
                                });
                            }}
                        >
                            SMS Preview
                        </Button>
                    </Group>
                )}

                {isSupport && (
                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            onClick={() => {
                                modals.open({
                                    title: <Title order={3}>Body Preview</Title>,
                                    size: "lg",
                                    children: (
                                        <ComposeFormProvider form={form}>
                                            <BodyPreview />
                                        </ComposeFormProvider>
                                    ),
                                });
                            }}
                        >
                            Support Body Preview
                        </Button>
                    </Group>
                )}

                <Suspense
                    fallback={
                        <Group>
                            {[200, 50, 150, 170, 120, 250].map((w, i) => (
                                <Skeleton key={i} h="lg" w={w} />
                            ))}
                        </Group>
                    }
                >
                    <AttachmentEditor />
                </Suspense>
            </Section>

            <Section label="Poll">
                <PollEditor />
            </Section>

            <Divider />
        </SectionProvider>
    );
};
